import React, { memo, useMemo } from 'react';
import {
  BaseEdge,
  ConnectionLineType,
  getBezierPath,
  getSmoothStepPath,
  getStraightPath
} from '@xyflow/react';
import { pinBus } from './workflowGraphModel';

/** Match pin colors in WfNodes.js (FLOW, DATA_IN/DATA_OUT, TOOL_PIN). */
function strokePairForSourceHandle(sourceHandleId) {
  const sid = sourceHandleId || '';
  const bus = pinBus(sid);
  if (bus === 'tool') {
    return { base: '#f59e0b', sel: '#fbbf24', glow: 'rgba(245,158,11,0.48)' };
  }
  if (bus === 'data') {
    if (sid === 'data-out') {
      return { base: '#22c55e', sel: '#4ade80', glow: 'rgba(34,197,94,0.45)' };
    }
    return { base: '#3b82f6', sel: '#60a5fa', glow: 'rgba(59,130,246,0.45)' };
  }
  return { base: '#6366f1', sel: '#a5b4fc', glow: 'rgba(99,102,241,0.45)' };
}

function selectedGlow(glowRgb) {
  return `drop-shadow(0 0 6px ${glowRgb})`;
}

function BleuEdge(props) {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    markerEnd,
    selected,
    sourceHandleId,
    animated
  } = props;

  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition
  });

  const { base, sel, glow } = strokePairForSourceHandle(sourceHandleId);
  const stroke = selected ? sel : base;
  const glowFilter = selected ? selectedGlow(glow) : undefined;

  const style = useMemo(() => ({
    stroke,
    strokeWidth: selected ? 2.5 : 2,
    filter: glowFilter,
    ...(animated
      ? { strokeDasharray: 5, animation: 'wf-edge-dashdraw 0.5s linear infinite' }
      : {})
  }), [stroke, selected, glowFilter, animated]);

  return (
    <BaseEdge
      id={id}
      path={edgePath}
      markerEnd={markerEnd}
      style={style}
    />
  );
}

/**
 * Connection preview while dragging — stroke follows source pin color (same rules as BleuEdge).
 */
export function BleuConnectionLine({
  fromX,
  fromY,
  toX,
  toY,
  fromPosition,
  toPosition,
  connectionLineType = ConnectionLineType.Bezier,
  connectionLineStyle,
  fromHandle,
  connectionStatus
}) {
  const pathParams = {
    sourceX: fromX,
    sourceY: fromY,
    sourcePosition: fromPosition,
    targetX: toX,
    targetY: toY,
    targetPosition: toPosition
  };

  let path;
  switch (connectionLineType) {
    case ConnectionLineType.Step:
      path = getSmoothStepPath({ ...pathParams, borderRadius: 0 })[0];
      break;
    case ConnectionLineType.SmoothStep:
      path = getSmoothStepPath(pathParams)[0];
      break;
    case ConnectionLineType.Straight:
      path = getStraightPath(pathParams)[0];
      break;
    case ConnectionLineType.SimpleBezier:
    case ConnectionLineType.Bezier:
    default:
      path = getBezierPath(pathParams)[0];
      break;
  }

  const { base, sel } = strokePairForSourceHandle(fromHandle?.id);
  const stroke = connectionStatus === 'valid' ? sel : base;

  return (
    <path
      d={path}
      fill="none"
      className="react-flow__connection-path"
      style={{
        ...connectionLineStyle,
        stroke,
        strokeWidth: connectionLineStyle?.strokeWidth ?? 2
      }}
    />
  );
}

export default memo(BleuEdge);
